import { useEffect, useRef, useState } from "react";
import { FaBoxes } from "react-icons/fa";
import {
  FiAlertCircle,
  FiArrowDownCircle,
  FiArrowUpCircle,
  FiFilter,
  FiList,
  FiSliders,
} from "react-icons/fi";
import * as XLSX from "xlsx";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import CartaoEstatistica from "../../../components/relatorios/CartaoEstatistica";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api, listarTudo } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

const RelatorioMovimentacaoEstoque = () => {
  const [dados, setDados] = useState({
    resumo: {
      total_entradas: 0,
      total_saidas: 0,
      total_ajustes: 0,
      valor_total_entradas: 0,
      valor_total_saidas: 0,
      quantidade_movimentacoes: 0,
    },
    movimentacoes: [],
    por_produto: [],
    por_tipo: [],
  });
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [abaAtiva, setAbaAtiva] = useState("movimentacoes");

  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [fazendas, setFazendas] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = {
    produtos: useRef(null),
    categorias: useRef(null),
    fazendas: useRef(null),
  };

  const [filtros, setFiltros] = useState({
    produtos: [],
    categorias: [],
    fazendas: [],
    tipo_movimentacao: "todos", // todos, entrada, saida, ajuste
    data_inicio: "",
    data_fim: "",
  });

  useEffect(() => {
    carregarOpcoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openDropdown &&
        dropdownRefs[openDropdown]?.current &&
        !dropdownRefs[openDropdown].current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  const carregarOpcoes = async () => {
    try {
      const [produtosRes, categoriasRes, fazendasRes] =
        await Promise.all([
          listarTudo("/almoxarifado/itens-estoque/"),
          listarTudo("/almoxarifado/categorias/"),
          api.get("/fazendas/fazendas/"),
        ]);

      setProdutos(
        Array.isArray(produtosRes.data) ? produtosRes.data : produtosRes.data.results || [],
      );
      setCategorias(
        Array.isArray(categoriasRes.data) ? categoriasRes.data : categoriasRes.data.results || [],
      );
      setFazendas(fazendasRes.data.results || []);
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
      setProdutos([]);
      setCategorias([]);
      setFazendas([]);
    }
  };

  const handleMultiSelect = (campo, id) => {
    setFiltros((prev) => {
      const current = prev[campo] || [];
      const updated = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      return { ...prev, [campo]: updated };
    });
  };

  const handleSelectAll = (campo, options) => {
    if (Array.isArray(options)) {
      const allIds = options.map((opt) => opt.id);
      setFiltros((prev) => ({ ...prev, [campo]: allIds }));
    }
  };

  const handleClearAll = (campo) => {
    setFiltros((prev) => ({ ...prev, [campo]: [] }));
  };

  const handleChange = (e) => {
    setFiltros({
      ...filtros,
      [e.target.name]: e.target.value,
    });
  };

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");

    try {
      const params = {};

      if (filtros.produtos && filtros.produtos.length > 0) {
        params.produtos = filtros.produtos.join(",");
      }
      if (filtros.categorias && filtros.categorias.length > 0) {
        params.categorias = filtros.categorias.join(",");
      }
      if (filtros.fazendas && filtros.fazendas.length > 0) {
        params.fazendas = filtros.fazendas.join(",");
      }
      if (filtros.tipo_movimentacao && filtros.tipo_movimentacao !== "todos") {
        params.tipo = filtros.tipo_movimentacao;
      }
      if (filtros.data_inicio) {
        params.data_inicio = filtros.data_inicio;
      }
      if (filtros.data_fim) {
        params.data_fim = filtros.data_fim;
      }

      console.log(
        "🌐 URL:",
        `/relatorios/almoxarifado/movimentacoes/?${new URLSearchParams(params)}`,
      );

      const response = await api.get("/relatorios/almoxarifado/movimentacoes/", {
        params: params,
      });

      console.log("🔍 Dados recebidos:", response.data);
      setDados(response.data);
      setShowModal(true);
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar o relatório. Verifique os filtros e tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      produtos: [],
      categorias: [],
      fazendas: [],
      tipo_movimentacao: "todos",
      data_inicio: "",
      data_fim: "",
    });
    setErro("");
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return "-";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);
  };

  const formatarData = (data) => {
    if (!data) return "-";
    return new Date(data).toLocaleDateString("pt-BR", { timeZone: "UTC" });
  };

  const getTipoClass = (tipo) => {
    if (tipo === "Entrada") return "text-green-600 font-bold";
    if (tipo === "Saída") return "text-red-600 font-bold";
    return "text-yellow-600";
  };

  const getTipoBgClass = (tipo) => {
    if (tipo === "Entrada") return "bg-green-100 text-green-800";
    if (tipo === "Saída") return "bg-red-100 text-red-800";
    return "bg-yellow-100 text-yellow-800";
  };

  const exportarExcel = () => {
    if (!dados || !dados.movimentacoes || dados.movimentacoes.length === 0) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const workbook = XLSX.utils.book_new();

    // Planilha de Movimentações
    const wsMovimentacoes = XLSX.utils.json_to_sheet(dados.movimentacoes);
    XLSX.utils.book_append_sheet(workbook, wsMovimentacoes, "Movimentacoes");

    // Planilha de Resumo por Produto
    if (dados.por_produto && dados.por_produto.length > 0) {
      const wsProduto = XLSX.utils.json_to_sheet(dados.por_produto);
      XLSX.utils.book_append_sheet(workbook, wsProduto, "Por Produto");
    }

    XLSX.writeFile(workbook, "relatorio_movimentacao_estoque.xlsx");
  };

  const exportarPDF = async () => {
    await exportarRelatorioPDF({
      titulo: "Relatório de Movimentação de Estoque",
      colunas: [
        { key: "data", label: "DATA" },
        { key: "produto", label: "PRODUTO" },
        { key: "categoria", label: "CATEGORIA" },
        { key: "tipo", label: "TIPO" },
        { key: "quantidade", label: "QUANTIDADE" },
        { key: "unidade", label: "UNID" },
        { key: "custo_unitario", label: "CUSTO UNIT" },
        { key: "valor_total", label: "VALOR TOTAL" },
        { key: "origem_destino", label: "ORIGEM/DESTINO" },
        { key: "responsavel", label: "RESPONSÁVEL" },
        { key: "observacao", label: "OBSERVAÇÃO" },
      ],
      dados: dados?.movimentacoes || [],
      periodo: formatarPeriodoFiltro(filtros.data_inicio, filtros.data_fim),
      formatarValor: (valor, coluna) =>
        coluna.key === "custo_unitario" || coluna.key === "valor_total"
          ? formatarMoeda(valor)
          : (valor ?? "-"),
    });
  };

  const imprimir = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatorio de Movimentacao de Estoque</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; font-size: 18px; }
            h2 { color: #555; font-size: 14px; margin-top: 20px; }
            .header-info { margin-bottom: 20px; }
            .periodo { background: #e8f4f8; padding: 10px; border-radius: 5px; margin-bottom: 20px; }
            .resumo-cards { display: flex; gap: 15px; margin-bottom: 25px; flex-wrap: wrap; }
            .card { background: #f8f9fa; padding: 15px; border-radius: 8px; text-align: center; min-width: 140px; }
            .card .valor { font-size: 22px; font-weight: bold; margin-top: 5px; }
            .card .label { font-size: 11px; color: #666; }
            .card.entrada .valor { color: #28a745; }
            .card.saida .valor { color: #dc3545; }
            .card.ajuste .valor { color: #ffc107; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; margin-bottom: 25px; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .entrada-badge { background-color: #d4edda; color: #155724; padding: 3px 8px; border-radius: 12px; font-size: 11px; }
            .saida-badge { background-color: #f8d7da; color: #721c24; padding: 3px 8px; border-radius: 12px; font-size: 11px; }
            .ajuste-badge { background-color: #fff3cd; color: #856404; padding: 3px 8px; border-radius: 12px; font-size: 11px; }
            .footer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; }
            @media print {
              body { margin: 0; padding: 20px; }
              .card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Relatorio de Movimentacao de Estoque</h1>
          <div class="header-info">
            <p><strong>Gerado em:</strong> ${new Date().toLocaleString()}</p>
          </div>

          ${
            filtros.data_inicio || filtros.data_fim
              ? `
          <div class="periodo">
            <strong>📅 Periodo analisado:</strong> ${filtros.data_inicio || "inicio"} a ${filtros.data_fim || "atual"}
          </div>
          `
              : ""
          }

          <div class="resumo-cards">
            <div class="card">
              <div class="label">📊 Total Movimentacoes</div>
              <div class="valor">${dados.resumo?.quantidade_movimentacoes || 0}</div>
            </div>
            <div class="card entrada">
              <div class="label">📥 Entradas</div>
              <div class="valor">${dados.resumo?.total_entradas || 0}</div>
            </div>
            <div class="card saida">
              <div class="label">📤 Saidas</div>
              <div class="valor">${dados.resumo?.total_saidas || 0}</div>
            </div>
            <div class="card ajuste">
              <div class="label">⚙️ Ajustes</div>
              <div class="valor">${dados.resumo?.total_ajustes || 0}</div>
            </div>
          </div>

          <div class="resumo-cards">
            <div class="card entrada">
              <div class="label">💰 Valor Entradas</div>
              <div class="valor">${formatarMoeda(dados.resumo?.valor_total_entradas)}</div>
            </div>
            <div class="card saida">
              <div class="label">💰 Valor Saidas</div>
              <div class="valor">${formatarMoeda(dados.resumo?.valor_total_saidas)}</div>
            </div>
          </div>

          <h2>📋 Movimentacoes de Estoque</h2>
          <table>
            <thead>
              <tr>
                <th>DATA</th>
                <th>PRODUTO</th>
                <th>CATEGORIA</th>
                <th>TIPO</th>
                <th>QUANTIDADE</th>
                <th>UNID</th>
                <th>VALOR TOTAL</th>
                <th>ORIGEM/DESTINO</th>
                <th>RESPONSAVEL</th>
              </tr>
            </thead>
            <tbody>
              ${dados.movimentacoes
                ?.map(
                  (item) => `
                <tr>
                  <td>${item.data || "-"}</td>
                  <td>${item.produto || "-"}</td>
                  <td>${item.categoria || "-"}</td>
                  <td><span class="${item.tipo === "Entrada" ? "entrada-badge" : item.tipo === "Saída" ? "saida-badge" : "ajuste-badge"}">${item.tipo || "-"}</span></td>
                  <td>${item.quantidade || 0}</td>
                  <td>${item.unidade || "-"}</td>
                  <td>${formatarMoeda(item.valor_total)}</td>
                  <td>${item.origem_destino || "-"}</td>
                  <td>${item.responsavel || "-"}</td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>

          ${
            dados.por_produto && dados.por_produto.length > 0
              ? `
          <h2>📊 Resumo por Produto</h2>
          <table>
            <thead><tr><th>PRODUTO</th><th>ENTRADAS</th><th>SAIDAS</th><th>SALDO</th></tr></thead>
            <tbody>
              ${dados.por_produto
                .map(
                  (p) => `
                <tr>
                  <td>${p.produto || "-"}</td>
                  <td>${p.total_entradas || 0} ${p.unidade}</td>
                  <td>${p.total_saidas || 0} ${p.unidade}</td>
                  <td>${p.saldo || 0} ${p.unidade}</td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
          `
              : ""
          }

          <div class="footer">Sistema de Gestao Agropecuaria - Relatorio Gerado Automaticamente</div>
          <script>
            window.onload = () => { window.print(); setTimeout(() => window.close(), 100); }
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const MultiSelect = ({
    label,
    options,
    selectedValues,
    fieldName,
    refProp,
  }) => {
    const optionsArray = Array.isArray(options) ? options : [];
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];

    const getSelectedNames = () => {
      const selected = optionsArray.filter((opt) =>
        selectedArray.includes(opt.id),
      );
      if (selected.length === 0) return `Selecione ${label.toLowerCase()}`;
      if (selected.length <= 2)
        return selected.map((s) => s.nome || s.username).join(", ");
      return `${selected.length} ${label.toLowerCase()} selecionados`;
    };

    return (
      <div className="relative" ref={refProp}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
        <div
          onClick={() =>
            setOpenDropdown(openDropdown === fieldName ? null : fieldName)
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-brand-green focus:border-transparent"
        >
          <span className="text-gray-700">{getSelectedNames()}</span>
          <span className="float-right">▼</span>
        </div>

        {openDropdown === fieldName && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => handleClearAll(fieldName)}
                className="text-sm text-brand-green hover:opacity-80 mr-3"
              >
                Limpar todos
              </button>
              <button
                onClick={() => handleSelectAll(fieldName, optionsArray)}
                className="text-sm text-brand-green hover:opacity-80"
              >
                Selecionar todos
              </button>
            </div>
            {optionsArray.map((option) => (
              <label
                key={option.id}
                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedArray.includes(option.id)}
                  onChange={() => handleMultiSelect(fieldName, option.id)}
                  className="mr-3 h-4 w-4 text-brand-green rounded border-gray-300"
                />
                <span className="text-gray-700">
                  {option.nome || option.username}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CabecalhoRelatorio
            icone={FaBoxes}
            titulo="Relatório de Movimentação de Estoque"
            descricao="Consulte todas as movimentações de estoque com entradas, saídas, custos e responsáveis"
          />

          <Cartao titulo="Filtros" icone={FiFilter} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MultiSelect
                label="Produtos"
                options={produtos}
                selectedValues={filtros.produtos}
                fieldName="produtos"
                refProp={dropdownRefs.produtos}
              />
              <MultiSelect
                label="Categorias"
                options={categorias}
                selectedValues={filtros.categorias}
                fieldName="categorias"
                refProp={dropdownRefs.categorias}
              />
              <MultiSelect
                label="Fazendas"
                options={fazendas}
                selectedValues={filtros.fazendas}
                fieldName="fazendas"
                refProp={dropdownRefs.fazendas}
              />

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Movimentacao
                </label>
                <select
                  name="tipo_movimentacao"
                  value={filtros.tipo_movimentacao}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                >
                  <option value="todos">Todos</option>
                  <option value="entrada">Entradas</option>
                  <option value="saida">Saidas</option>
                  <option value="ajuste">Ajustes</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Inicio
                </label>
                <input
                  type="date"
                  name="data_inicio"
                  value={filtros.data_inicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Fim
                </label>
                <input
                  type="date"
                  name="data_fim"
                  value={filtros.data_fim}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            {erro && (
              <div className="mt-6 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <FiAlertCircle size={18} className="shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <BotaoRelatorio
                variante="primario"
                onClick={buscarDados}
                carregando={carregando}
                className="flex-1"
              >
                Gerar Relatório
              </BotaoRelatorio>
              <BotaoRelatorio variante="secundario" onClick={limparFiltros}>
                Limpar Filtros
              </BotaoRelatorio>
            </div>
          </Cartao>
        </div>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{ maxHeight: "calc(100vh - 4rem)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">
                Relatorio de Movimentacao de Estoque
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center"
              >
                ✕
              </button>
            </div>

            <div className="border-b border-gray-200 px-6">
              <div className="flex gap-4">
                <button
                  onClick={() => setAbaAtiva("movimentacoes")}
                  className={`py-3 px-4 font-medium ${abaAtiva === "movimentacoes" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"}`}
                >
                  📋 Movimentacoes
                </button>
                <button
                  onClick={() => setAbaAtiva("produtos")}
                  className={`py-3 px-4 font-medium ${abaAtiva === "produtos" ? "border-b-2 border-green-500 text-green-600" : "text-gray-500"}`}
                >
                  📦 Por Produto
                </button>
                <button
                  onClick={() => setAbaAtiva("resumo")}
                  className={`py-3 px-4 font-medium ${abaAtiva === "resumo" ? "border-b-2 border-purple-500 text-purple-600" : "text-gray-500"}`}
                >
                  📊 Resumo Financeiro
                </button>
              </div>
            </div>

            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3">
              <BotaoRelatorio variante="excel" onClick={exportarExcel}>
                Exportar para Excel
              </BotaoRelatorio>
              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>
                Exportar para PDF
              </BotaoRelatorio>
              <BotaoRelatorio variante="imprimir" onClick={imprimir}>
                Imprimir
              </BotaoRelatorio>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {abaAtiva === "movimentacoes" && (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <CartaoEstatistica
                      icone={FiList}
                      rotulo="Total Movimentações"
                      valor={dados.resumo?.quantidade_movimentacoes || 0}
                      cor="azul"
                    />
                    <CartaoEstatistica
                      icone={FiArrowDownCircle}
                      rotulo="Entradas"
                      valor={dados.resumo?.total_entradas || 0}
                      cor="verde"
                    />
                    <CartaoEstatistica
                      icone={FiArrowUpCircle}
                      rotulo="Saídas"
                      valor={dados.resumo?.total_saidas || 0}
                      cor="vermelho"
                    />
                    <CartaoEstatistica
                      icone={FiSliders}
                      rotulo="Ajustes"
                      valor={dados.resumo?.total_ajustes || 0}
                      cor="dourado"
                    />
                  </div>

                  {dados.movimentacoes?.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              DATA
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              PRODUTO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              CATEGORIA
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              TIPO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              QUANTIDADE
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              UNID
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              VALOR TOTAL
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              ORIGEM/DESTINO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              RESPONSAVEL
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              OBSERVACAO
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.movimentacoes?.map((item, idx) => (
                            <tr
                              key={idx}
                              className="border-b border-gray-100 hover:bg-gray-50 transition"
                            >
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.data || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-700">
                                {item.produto || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.categoria || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs ${getTipoBgClass(item.tipo)}`}
                                >
                                  {item.tipo || "-"}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.quantidade || 0}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.unidade || "-"}
                              </td>
                              <td
                                className={`px-4 py-3 text-sm ${getTipoClass(item.tipo)}`}
                              >
                                {formatarMoeda(item.valor_total)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.origem_destino || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.responsavel || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.observacao || "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio
                      titulo="Nenhuma movimentação encontrada"
                      descricao="Ajuste os filtros selecionados e tente novamente."
                    />
                  )}
                </div>
              )}

              {abaAtiva === "produtos" && (
                <div>
                  {dados.por_produto?.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              PRODUTO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              CATEGORIA
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              ENTRADAS
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              SAIDAS
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              SALDO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              VALOR MOVIMENTADO
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.por_produto?.map((prod, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              <td className="px-4 py-3 text-sm font-medium text-gray-700">
                                {prod.produto || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {prod.categoria || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-green-600">
                                {prod.total_entradas || 0} {prod.unidade}
                              </td>
                              <td className="px-4 py-3 text-sm text-red-600">
                                {prod.total_saidas || 0} {prod.unidade}
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-gray-700">
                                {prod.saldo || 0} {prod.unidade}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {formatarMoeda(prod.valor_movimentado)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio
                      titulo="Nenhum produto encontrado"
                      descricao="Ajuste os filtros selecionados e tente novamente."
                    />
                  )}
                </div>
              )}

              {abaAtiva === "resumo" && (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                      <h4 className="text-lg font-semibold text-green-800 mb-4">
                        📥 Entradas
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span>Quantidade de Entradas:</span>
                          <span className="font-bold">
                            {dados.resumo?.total_entradas || 0}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Valor Total Entradas:</span>
                          <span className="font-bold text-green-600">
                            {formatarMoeda(dados.resumo?.valor_total_entradas)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Ticket Medio:</span>
                          <span className="font-bold">
                            {formatarMoeda(
                              (dados.resumo?.valor_total_entradas || 0) /
                                (dados.resumo?.total_entradas || 1),
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                      <h4 className="text-lg font-semibold text-red-800 mb-4">
                        📤 Saidas
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span>Quantidade de Saidas:</span>
                          <span className="font-bold">
                            {dados.resumo?.total_saidas || 0}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Valor Total Saidas:</span>
                          <span className="font-bold text-red-600">
                            {formatarMoeda(dados.resumo?.valor_total_saidas)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Ticket Medio:</span>
                          <span className="font-bold">
                            {formatarMoeda(
                              (dados.resumo?.valor_total_saidas || 0) /
                                (dados.resumo?.total_saidas || 1),
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">
                      📊 Totalizadores
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {dados.resumo?.quantidade_movimentacoes || 0}
                        </div>
                        <div className="text-xs text-gray-600">
                          Total Movimentacoes
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {formatarMoeda(dados.resumo?.valor_total_entradas)}
                        </div>
                        <div className="text-xs text-gray-600">
                          Total Entradas
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {formatarMoeda(dados.resumo?.valor_total_saidas)}
                        </div>
                        <div className="text-xs text-gray-600">
                          Total Saidas
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {formatarMoeda(
                            (dados.resumo?.valor_total_entradas || 0) -
                              (dados.resumo?.valor_total_saidas || 0),
                          )}
                        </div>
                        <div className="text-xs text-gray-600">Resultado</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">
                          {formatarMoeda(
                            (dados.resumo?.valor_total_entradas || 0) /
                              (dados.resumo?.total_entradas || 1),
                          )}
                        </div>
                        <div className="text-xs text-gray-600">
                          Ticket Medio Entrada
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">
                          {formatarMoeda(
                            (dados.resumo?.valor_total_saidas || 0) /
                              (dados.resumo?.total_saidas || 1),
                          )}
                        </div>
                        <div className="text-xs text-gray-600">
                          Ticket Medio Saida
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end">
              <BotaoRelatorio variante="secundario" onClick={() => setShowModal(false)}>
                Fechar
              </BotaoRelatorio>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioMovimentacaoEstoque;
